import { shallowRef } from 'vue';
import { zhCN } from './zh-CN';

export const LOCALE_STORAGE_KEY = 'xcreate-locale';
const supportedLocales = ['zh-CN', 'en-US'];
const localeState = shallowRef(supportedLocales.includes(localStorage.getItem(LOCALE_STORAGE_KEY))
    ? localStorage.getItem(LOCALE_STORAGE_KEY)
    : 'zh-CN');
let observer;
let translating = false;
const originalText = new WeakMap();
const renderedText = new WeakMap();
const originalAttributes = new WeakMap();
const renderedAttributes = new WeakMap();
const translatedNodes = new Set();
const translatedElements = new Set();
const translatedAttributes = ['placeholder', 'title', 'aria-label'];
const normalizePhrase = (value) => String(value || '')
    .normalize('NFKC')
    .replace(/\s+/g, ' ')
    .trim()
    .toLocaleLowerCase('en-US');
const normalizedZhCN = new Map(
    Object.entries(zhCN).map(([source, translation]) => [
        normalizePhrase(source),
        translation
    ])
);

const dynamicTranslations = [
    [/^(\d+)\s+Tasks?$/i, (_, count) => `${count} 个任务`],
    [/^(\d+)\s+Projects?$/i, (_, count) => `${count} 个项目`],
    [/^(\d+)\s+watchers?$/i, (_, count) => `${count} 位关注人`],
    [/^Attachments\((\d+)\)$/i, (_, count) => `附件（${count}）`],
    [/^Create (\d+) Subtasks?$/i, (_, count) => `创建 ${count} 个子任务`],
    [/^No (.+) found$/i, (_, subject) => `暂无${translatePhrase(subject)}`],
    [/^Add (.+)$/i, (_, subject) => `添加${translatePhrase(subject)}`],
    [/^Create (.+)$/i, (_, subject) => `新建${translatePhrase(subject)}`],
    [/^Edit (.+)$/i, (_, subject) => `编辑${translatePhrase(subject)}`],
    [/^Delete (.+)$/i, (_, subject) => `删除${translatePhrase(subject)}`],
    [/^Select (.+)$/i, (_, subject) => `选择${translatePhrase(subject)}`],
    [/^Search (.+)$/i, (_, subject) => `搜索${translatePhrase(subject)}`],
    [/^The (.+) field is required\.?$/i, (_, field) => `请输入${translatePhrase(field)}`],
    [/^Are you sure you want to delete (.+)\??$/i, (_, subject) => `确定要删除${translatePhrase(subject)}吗？`]
];

export function getLocale() {
    return localeState.value;
}

export function translatePhrase(value) {
    // Reading the ref here makes every template/computed value that calls
    // translatePhrase react to a language switch. The DOM observer remains as
    // a compatibility layer for historical hard-coded text nodes.
    if (localeState.value !== 'zh-CN' || typeof value !== 'string') return value;
    const phrase = value.trim();
    if (!phrase) return value;
    if (zhCN[phrase]) return zhCN[phrase];
    const normalizedTranslation = normalizedZhCN.get(normalizePhrase(phrase));
    if (normalizedTranslation) return normalizedTranslation;
    for (const [pattern, replacement] of dynamicTranslations) {
        if (pattern.test(phrase)) return phrase.replace(pattern, replacement);
    }
    return phrase;
}

function isExcluded(element) {
    return !element || element.closest?.('[data-no-localize], script, style, code, pre, [contenteditable="true"]');
}

function translateTextNode(node) {
    if (!node?.parentElement || isExcluded(node.parentElement)) return;
    if (!originalText.has(node) || (renderedText.has(node) && node.nodeValue !== renderedText.get(node))) {
        originalText.set(node, node.nodeValue);
    }
    translatedNodes.add(node);
    const original = originalText.get(node);
    if (localeState.value === 'en-US') {
        if (node.nodeValue !== original) node.nodeValue = original;
        renderedText.set(node, original);
        return;
    }
    const leading = original.match(/^\s*/)?.[0] || '';
    const trailing = original.match(/\s*$/)?.[0] || '';
    const translated = translatePhrase(original.trim());
    const nextValue = `${leading}${translated}${trailing}`;
    if (node.nodeValue !== nextValue) node.nodeValue = nextValue;
    renderedText.set(node, nextValue);
}

function translateElementAttributes(element) {
    if (!(element instanceof Element) || isExcluded(element)) return;
    let originals = originalAttributes.get(element);
    let rendered = renderedAttributes.get(element);
    if (!originals) {
        originals = {};
        originalAttributes.set(element, originals);
    }
    if (!rendered) {
        rendered = {};
        renderedAttributes.set(element, rendered);
    }
    translatedAttributes.forEach((attribute) => {
        if (!element.hasAttribute(attribute)) return;
        const current = element.getAttribute(attribute);
        // Vue can update title/placeholder/aria-label after the compatibility
        // observer has translated the element. Treat values that differ from
        // our last rendered value as the component's new source text instead
        // of restoring a stale attribute from the first render.
        if (!(attribute in originals) || (attribute in rendered && current !== rendered[attribute])) {
            originals[attribute] = current;
        }
        const original = originals[attribute];
        const nextValue = localeState.value === 'en-US' ? original : translatePhrase(original);
        if (current !== nextValue) element.setAttribute(attribute, nextValue);
        rendered[attribute] = nextValue;
    });
    translatedElements.add(element);
}

function translateTree(root = document.body) {
    if (!root) return;
    translating = true;
    try {
        if (root.nodeType === Node.TEXT_NODE) translateTextNode(root);
        if (root.nodeType === Node.ELEMENT_NODE) {
            translateElementAttributes(root);
            root.querySelectorAll('*').forEach(translateElementAttributes);
            const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
            let node;
            while ((node = walker.nextNode())) translateTextNode(node);
        }
    } finally {
        translating = false;
    }
}

export function applyLocale() {
    document.documentElement.lang = localeState.value;
    if (localeState.value === 'en-US') {
        translating = true;
        translatedNodes.forEach((node) => {
            if (node.isConnected && originalText.has(node)) {
                node.nodeValue = originalText.get(node);
                renderedText.set(node, node.nodeValue);
            }
        });
        translatedElements.forEach((element) => {
            if (!element.isConnected) return;
            const originals = originalAttributes.get(element) || {};
            const rendered = renderedAttributes.get(element) || {};
            Object.entries(originals).forEach(([attribute, value]) => {
                element.setAttribute(attribute, value);
                rendered[attribute] = value;
            });
            renderedAttributes.set(element, rendered);
        });
        translating = false;
    } else {
        translateTree(document.body);
    }
}

export function setLocale(locale) {
    localeState.value = supportedLocales.includes(locale) ? locale : 'zh-CN';
    localStorage.setItem(LOCALE_STORAGE_KEY, localeState.value);
    applyLocale();
    window.dispatchEvent(new CustomEvent('xcreate:locale-changed', {detail: {locale: localeState.value}}));
}

export function initLocalization() {
    applyLocale();
    observer?.disconnect();
    observer = new MutationObserver((mutations) => {
        if (translating) return;
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => translateTree(node));
            if (mutation.type === 'characterData') translateTextNode(mutation.target);
            if (mutation.type === 'attributes') translateElementAttributes(mutation.target);
        });
    });
    observer.observe(document.body, {subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: translatedAttributes});
}
